import { ChevronRight, type LucideIcon } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef, type ReactElement, type ReactNode } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/primitives/collapsible";
import { cn } from "@/shared/lib/utils";

// `title` is omitted from the DOM props on purpose: ours is the header node, not the browser's tooltip
// string, and letting the two collide would silently narrow it to `string`.
export interface ColocaAccordionItemProps extends Omit<ComponentPropsWithoutRef<"div">, "title"> {
  /** Header content. Keep it short — the whole header is the click target. */
  title: ReactNode;
  /** Icon rendered before the title. A prop, never children (design-system rule). */
  icon?: LucideIcon;
  /** Right-hand header slot: a count, a badge, "3/12". */
  meta?: ReactNode;
  /** Initial state when uncontrolled. */
  defaultOpen?: boolean;
  /**
   * Controlled state. Needed whenever something outside the header decides visibility — searching, for
   * example: the panel UNMOUNTS while closed, so a match inside a collapsed group would be invisible.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** Base UI's `render` — this primitive's equivalent of `asChild`. */
  render?: ReactElement;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
}

/**
 * One disclosure panel: a always-visible header that toggles its content.
 *
 * Wraps the Base UI collapsible primitive, which supplies the ARIA wiring (`aria-expanded` + `aria-controls`
 * on the trigger, a labelled region for the panel) that a hand-rolled `<div>` would not.
 */
export const ColocaAccordionItem = forwardRef<HTMLDivElement, ColocaAccordionItemProps>(
  function ColocaAccordionItem(
    {
      title,
      icon: Icon,
      meta,
      defaultOpen,
      open,
      onOpenChange,
      disabled,
      render,
      className,
      contentClassName,
      children,
      ...rest
    },
    ref,
  ) {
    return (
      <Collapsible
        ref={ref}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        disabled={disabled}
        render={render}
        className={cn("overflow-hidden rounded-md border border-border bg-card", className)}
        {...rest}
      >
        {/* The rotation targets the chevron through a descendant selector rather than the trigger itself:
            `aria-expanded` lives on the trigger, and the trigger also holds the title and the meta slot, so
            rotating it would spin the whole header. */}
        <CollapsibleTrigger
          className={cn(
            "flex w-full items-center gap-2 px-3 py-2 text-left transition-colors",
            "hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            "aria-expanded:bg-muted/40 disabled:cursor-default disabled:opacity-60",
            "[&[aria-expanded=true]>svg:first-of-type]:rotate-90",
          )}
        >
          <ChevronRight
            size={13}
            aria-hidden
            className="shrink-0 text-muted-foreground transition-transform"
          />
          {Icon && <Icon size={13} aria-hidden className="shrink-0 text-muted-foreground" />}
          <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-foreground">{title}</span>
          {meta != null && <span className="ml-auto shrink-0">{meta}</span>}
        </CollapsibleTrigger>
        <CollapsibleContent className={cn("border-t border-border p-2", contentClassName)}>
          {children}
        </CollapsibleContent>
      </Collapsible>
    );
  },
);

export interface ColocaAccordionProps extends ComponentPropsWithoutRef<"div"> {
  children: ReactNode;
  className?: string;
}

/**
 * Container for a stack of {@link ColocaAccordionItem}. Each item owns its own open state, so several can be
 * expanded at once — the right default when the panels are peers rather than steps.
 */
export const ColocaAccordion = forwardRef<HTMLDivElement, ColocaAccordionProps>(function ColocaAccordion(
  { children, className, ...rest },
  ref,
) {
  return (
    <div ref={ref} className={cn("flex flex-col gap-1.5", className)} {...rest}>
      {children}
    </div>
  );
});
